import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const CATEGORIES = ['All', 'Partnership', 'Support', 'Feedback', 'Suggestion', 'Others'];

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  }).format(d);
};

const AdminContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingId, setSavingId] = useState('');

  const [categoryFilter, setCategoryFilter] = useState('All');
  const [readFilter, setReadFilter] = useState('all');
  const [expandedId, setExpandedId] = useState('');
  const [noteDrafts, setNoteDrafts] = useState({});

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (categoryFilter !== 'All') params.set('category', categoryFilter);
      if (readFilter === 'read') params.set('is_read', 'true');
      if (readFilter === 'unread') params.set('is_read', 'false');
      const res = await axios.get(`/api/contact-messages${params.toString() ? `?${params.toString()}` : ''}`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const total = messages.length;
    const unread = messages.filter((m) => !m.is_read).length;
    return { total, unread, read: total - unread };
  }, [messages]);

  const handleToggleRead = async (msg) => {
    try {
      setSavingId(msg.id);
      const res = await axios.patch(`/api/contact-messages/${msg.id}`, { is_read: !msg.is_read });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? res.data : m)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update message');
    } finally {
      setSavingId('');
    }
  };

  const handleSaveNote = async (id) => {
    try {
      setSavingId(id);
      const res = await axios.patch(`/api/contact-messages/${id}`, {
        admin_notes: noteDrafts[id] ?? '',
      });
      setMessages((prev) => prev.map((m) => (m.id === id ? res.data : m)));
      setSuccess('Note saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingId('');
    }
  };

  const categoryBadgeVariant = (cat) => {
    const map = {
      Partnership: 'primary',
      Support: 'danger',
      Feedback: 'success',
      Suggestion: 'info',
      Others: 'secondary',
    };
    return map[cat] || 'secondary';
  };

  return (
    <Container className="py-4">
      <h2 className="mb-1">Write to Us — Inbox</h2>
      <p className="text-muted mb-4">Messages submitted via the footer "Write to Us" form.</p>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats */}
      <Row className="g-3 mb-4">
        {[
          { label: 'Total', value: stats.total, bg: 'primary' },
          { label: 'Unread', value: stats.unread, bg: 'warning' },
          { label: 'Read', value: stats.read, bg: 'success' },
        ].map(({ label, value, bg }) => (
          <Col xs={6} sm={4} md={3} key={label}>
            <Card className={`text-white bg-${bg} text-center py-2`}>
              <Card.Body className="p-2">
                <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>{value}</div>
                <div className="small">{label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Row className="g-2 mb-3 align-items-end">
        <Col xs={12} sm={6} md={4}>
          <Form.Label className="fw-semibold small">Category</Form.Label>
          <Form.Select size="sm" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Form.Label className="fw-semibold small">Status</Form.Label>
          <Form.Select size="sm" value={readFilter} onChange={(e) => setReadFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="outline-primary" onClick={loadMessages}>Apply</Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : messages.length === 0 ? (
        <Alert variant="info">No messages found.</Alert>
      ) : (
        <Table responsive bordered hover size="sm" className="align-middle">
          <thead className="table-dark">
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((msg) => (
              <React.Fragment key={msg.id}>
                <tr style={{ fontWeight: msg.is_read ? 'normal' : '600' }}>
                  <td className="text-nowrap small">{formatDateTime(msg.created_at)}</td>
                  <td><Badge bg={categoryBadgeVariant(msg.category)}>{msg.category}</Badge></td>
                  <td className="small">{msg.email}</td>
                  <td className="small">{msg.phone || '-'}</td>
                  <td className="small">{msg.subject}</td>
                  <td>
                    <Badge bg={msg.is_read ? 'success' : 'warning'} text={msg.is_read ? undefined : 'dark'}>
                      {msg.is_read ? 'Read' : 'Unread'}
                    </Badge>
                  </td>
                  <td className="text-nowrap">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-1"
                      onClick={() => setExpandedId(expandedId === msg.id ? '' : msg.id)}
                    >
                      {expandedId === msg.id ? 'Hide' : 'View'}
                    </Button>
                    <Button
                      size="sm"
                      variant={msg.is_read ? 'outline-warning' : 'outline-success'}
                      disabled={savingId === msg.id}
                      onClick={() => handleToggleRead(msg)}
                    >
                      {msg.is_read ? 'Mark Unread' : 'Mark Read'}
                    </Button>
                  </td>
                </tr>
                {expandedId === msg.id && (
                  <tr>
                    <td colSpan={7} className="bg-light">
                      <div className="p-2">
                        <p className="mb-2"><strong>Message:</strong></p>
                        <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                        <Form.Label className="fw-semibold small">Admin Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          size="sm"
                          value={noteDrafts[msg.id] ?? (msg.admin_notes || '')}
                          onChange={(e) =>
                            setNoteDrafts((prev) => ({ ...prev, [msg.id]: e.target.value }))
                          }
                          placeholder="Add internal notes…"
                        />
                        <Button
                          size="sm"
                          className="mt-2"
                          variant="primary"
                          disabled={savingId === msg.id}
                          onClick={() => handleSaveNote(msg.id)}
                        >
                          {savingId === msg.id ? 'Saving…' : 'Save Note'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AdminContactMessages;
